import { useContext, useEffect, useRef, useState } from 'react';

import { forEach } from 'lib/helpers';

import { gsap } from 'gsap';
import { Section, SectionHeader, Title, ViewAll, Wrapper } from './components';

import filtersMap from 'components/FiltersModal/filtersMap';
import PillButton from 'components/buttons/PillButton';
import CaretBase from 'components/svg/Caret';

import Header from 'components/FiltersModal/Header';
import { getParseMap } from 'components/SelectedFilters';
import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import { ALL, FILTER_MODE, QUALIFIERS, START_DATE } from 'contexts/CruiseSearchContext/constants';
import { getBrandUISettings } from 'lib/utilities/Brands';
import { useFeatureFlags } from '../../../../hooks/LaunchDarkly/useFeatureFlags';

/**
 * All
 *
 * The All filters component only appears on mobile
 * and serves:
 * - To show selected filters (which is hidden on mobile)
 * - As a starting place for filtering on mobile.
 *
 * The UI has been completed but the logic for which
 * filters are the default in this view is not=.
 */

function All() {
	const {
		searchSelecting,
		qualifiersApplied,
		removeAllFilters,
		filterOptionsDictionary,
		removeFromSearchSelectingFilter,
		removeFromQualifiersSelecting,
		applyFilters,
		globalization,
		openFilters,
		brand,
		locale,
	} = useContext(CruiseSearchContext);

	const { showInOrderThePillsOfTheFilters, showMyOffers } = useFeatureFlags();

	const { uiSettings } = getBrandUISettings(brand);

	const { generalDictionary, filtersDictionary } = globalization?.dictionaries || {};

	const { labelAllFiltersTitle } = generalDictionary;

	const { labelDeparturePortPrepender, labelNightsAppender, labelCouponPrepender } = filtersDictionary;

	const parseMap = getParseMap({
		labelDeparturePortPrepender,
		labelNightsAppender,
		labelCouponPrepender,
	});

	const sectionsRef = useRef();

	const [filtersToShow, setFiltersToShow] = useState({});

	useEffect(() => {
		const els = sectionsRef.current?.children || [];
		gsap.from(els, {
			duration: 0.75,
			opacity: 0,
			y: '10px',
			ease: 'power2.inOut',
			stagger: 0.25,
		});
	}, []);

	useEffect(() => {
		let newFilters = {};
		if (showInOrderThePillsOfTheFilters) {
			Object.assign(newFilters, { all: filtersMap.all });
			const filters = showInOrderThePillsOfTheFilters.split('|');
			forEach(filters, (item) => {
				const dataFilter = filtersMap[item];
				const filterToAdd = {
					[item]: {
						key: item,
						label: dataFilter.label,
						showSearchBar: dataFilter.showSearchBar,
						component: dataFilter.component,
						ButtonComponent: dataFilter.ButtonComponent,
						labelKey: dataFilter.labelKey,
					},
				};

				newFilters = { ...newFilters, ...filterToAdd };
			});
		} else {
			newFilters = { ...filtersMap };
		}

		setFiltersToShow(newFilters);
	}, [showInOrderThePillsOfTheFilters]);

	const sections = () => {
		return Object.values(filtersToShow).map((filter) => {
			const { key } = filter;
			if (key === ALL) return;
			if (key === filtersMap.qualifiers.key && !showMyOffers) return;
			const openFilter = () => {
				openFilters(key, FILTER_MODE.ALL_FILTERS);
			};
			const labelKey = filtersMap[key]?.labelKey || '';
			const label = filtersDictionary[labelKey] || '';
			const testId = filtersMap[key]?.dataTestId ? `mobile-${filtersMap[key].dataTestId}` : '';
			const allFilters = {
				...searchSelecting,
				[QUALIFIERS]: qualifiersApplied,
			};

			return (
				<Section key={labelKey} onClick={openFilter} gap={uiSettings.filtersModal.filtersGrid.gap} data-testid={testId}>
					<SectionHeader>
						<Title title={label} className="sectionHeaderTitle" />
						<ViewAll>
							<CaretBase right viewAll />
						</ViewAll>
					</SectionHeader>

					{allFilters[key]?.map((value) => {
						const qualifier = key === QUALIFIERS;
						const onClick = (event) => {
							event.stopPropagation();
							if (qualifier) {
								removeFromQualifiersSelecting(value, applyFilters);
								return;
							}
							removeFromSearchSelectingFilter(key, value, applyFilters);
						};

						const parser = parseMap[key];
						if (!parser) return null;

						const { makeLabel, labelOptions } = parser;
						const filterOption = filterOptionsDictionary[key];
						const label = filterOption.key === START_DATE ? makeLabel(value, locale) : makeLabel(value, labelOptions, filterOption, filterOption.key);
						return (
							<PillButton key={label} onClick={onClick} selected showX>
								{label}
							</PillButton>
						);
					})}
				</Section>
			);
		});
	};

	return (
		<>
			<Header title={labelAllFiltersTitle} onReset={removeAllFilters} />
			<Wrapper ref={sectionsRef}>{sections()}</Wrapper>
		</>
	);
}

All.propTypes = {};

export default All;
